import React, { useEffect, useState } from "react";
import {
  Grid,
  TextField,
  Paper,
  Typography,
  Pagination,
  IconButton,
  MenuItem,
  Menu,
  Button,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Chip,
  FormControl,
  InputLabel,
  Select,
  Card,
  CardContent,
  CardMedia,
  CardActions,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Divider,
  Popover,
} from "@mui/material";
import { useDispatch } from "react-redux";
import { getAllPosts, deletePostById } from "store/actions";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import AddIcon from "@mui/icons-material/Add";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import FlagIcon from "@mui/icons-material/Flag";
import ConfirmDeleteDialog from "ui-component/confirmDeleteDialog";
import CreatePostModal from "./CreatePostModal";
import { phaseOptions, topicOptions } from "utils/constants";
const PostsManager = () => {
  const [page, setPage] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [topicFilter, setTopicFilter] = useState("");
  const [phaseFilter, setPhaseFilter] = useState("");
  const dispatch = useDispatch();
  const [postsData, setPostsData] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const [menuAnchorEls, setMenuAnchorEls] = useState({});
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [selectedPostId, setSelectedPostId] = useState(null);
  const [openDetailDialog, setOpenDetailDialog] = useState(false);
  const [selectedPost, setSelectedPost] = useState(null);
  const [openCreateModal, setOpenCreateModal] = useState(false);
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("desc");
  const [engagementDialog, setEngagementDialog] = useState({ open: false, type: null, post: null });
  const [popAnchor, setPopAnchor] = useState(null);
  const [popType, setPopType] = useState(null);
  const [popPost, setPopPost] = useState(null);

  useEffect(() => {
    getAllPostsData();
  }, [dispatch, page]);

  const getAllPostsData = () => {
    dispatch(
      getAllPosts(page + 1, 10, searchTerm, encodeURIComponent(topicFilter), phaseFilter, (result) => {
        setPostsData(result.posts);
        setTotalPages(result.totalPages);
      })
    );
  };

  const handleChangePage = (event, value) => {
    setPage(value - 1);
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setPage(0);
  };

  const handleTopicFilterChange = (event) => {
    setTopicFilter(event.target.value);
    setPage(0);
  };

  const handlePhaseFilterChange = (event) => {
    setPhaseFilter(event.target.value);
    setPage(0);
  };

  useEffect(() => {
    let timerOut = setTimeout(() => {
      getAllPostsData();
    }, 800);
    return () => clearTimeout(timerOut);
  }, [searchTerm, topicFilter, phaseFilter]);

  const handleMenuClick = (event, id) => {
    setMenuAnchorEls((prev) => ({ ...prev, [id]: event.currentTarget }));
  };

  const handleMenuClose = (id) => {
    setMenuAnchorEls((prev) => ({ ...prev, [id]: null }));
  };

  const handleView = (post) => {
    setSelectedPost(post);
    setOpenDetailDialog(true);
    handleMenuClose(post._id);
  };

  const handleDelete = (id) => {
    handleMenuClose(id);
    setSelectedPostId(id);
    setOpenConfirmDialog(true);
  };

  const confirmDeletePost = () => {
    dispatch(
      deletePostById(selectedPostId, (result) => {
        if (result?.code === 200) getAllPostsData();
      })
    );
    setOpenConfirmDialog(false);
  };

  const sortedPostsData = [...postsData].sort((a, b) => {
    const valA = a[sortBy] ?? "";
    const valB = b[sortBy] ?? "";

    if (sortBy === "createdAt") {
      return sortOrder === "asc"
        ? new Date(valA) - new Date(valB)
        : new Date(valB) - new Date(valA);
    }

    if (typeof valA === "string" && typeof valB === "string") {
      return sortOrder === "asc"
        ? valA.localeCompare(valB)
        : valB.localeCompare(valA);
    }

    if (typeof valA === "number" && typeof valB === "number") {
      return sortOrder === "asc" ? valA - valB : valB - valA;
    }

    return 0;
  });

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(column);
      setSortOrder("desc");
    }
  };

  const formatDate = (date) => {
    if (!date) return "-";
    return new Date(date).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const openPopover = (anchorEl, post, type) => {
    setPopAnchor(anchorEl);
    setPopType(type);
    setPopPost(post);
  };
  const closePopover = () => {
    setPopAnchor(null);
    setPopType(null);
    setPopPost(null);
  };

  const renderEngagementList = (post, type) => {
    if (!post) return null;

    const formatDate = (date) =>
      date
        ? new Date(date).toLocaleString(undefined, {
          month: "short",
          day: "numeric",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        })
        : "";

    const EmptyState = ({ message }) => (
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ textAlign: "center", py: 1 }}
      >
        {message}
      </Typography>
    );

    const CompactListItem = ({ leftAvatar, primary, secondary, date }) => (
      <ListItem
        sx={{
          py: 0.8,
          px: 1.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, flex: 1 }}>
          {leftAvatar}
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {primary}
            </Typography>
            {secondary && (
              <Typography variant="caption" color="text.secondary">
                {secondary}
              </Typography>
            )}
          </Box>
        </Box>
        {date && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ whiteSpace: "nowrap", ml: 1 }}
          >
            {formatDate(date)}
          </Typography>
        )}
      </ListItem>
    );

    const renderList = (items, renderItem) => (
      <Paper
        elevation={0}
        sx={{
          borderRadius: 2,
          border: "1px solid",
          borderColor: "divider",
          backgroundColor: "background.paper",
          overflow: "hidden",
        }}
      >
        <List disablePadding>
          {items.map((item, idx) => (
            <React.Fragment key={idx}>
              {renderItem(item)}
              {idx < items.length - 1 && <Divider component="li" />}
            </React.Fragment>
          ))}
        </List>
      </Paper>
    );

    // ---------- Likes ----------
    if (type === "likes") {
      const likes = post?.likes || [];
      if (likes.length === 0) return <EmptyState message="No likes yet" />;

      return renderList(likes, (like) => {
        const user = like?.user || like;
        return (
          <CompactListItem
            leftAvatar={<Avatar src={user?.profileImage?.signedUrl} alt={user?.name} />}
            primary={user?.name || "Unknown"}
            secondary={user?.email}
          />
        );
      });
    }

    // ---------- Comments ----------
    if (type === "comments") {
      const comments = post?.comments || [];
      if (comments.length === 0) return <EmptyState message="No comments yet" />;

      return renderList(comments, (comment) => {
        const user = comment?.user;
        return (
          <CompactListItem
            leftAvatar={<Avatar src={user?.profileImage?.signedUrl} alt={user?.name} />}
            primary={user?.name || "Unknown"}
            secondary={comment?.text || comment?.comment || ""}
            date={comment?.createdAt}
          />
        );
      });
    }

    // ---------- Reports ----------
    if (type === "reports") {
      const reports = post?.reports || [];
      if (reports.length === 0) return <EmptyState message="No reports" />;

      return renderList(reports, (report) => {
        const user = report?.user;
        return (
          <CompactListItem
            leftAvatar={<Avatar src={user?.profileImage?.signedUrl} alt={user?.name} />}
            primary={user?.name || "Unknown"}
            secondary={`Reason: ${report?.reason || "-"}`}
            date={report?.createdAt}
          />
        );
      });
    }

    return null;
  };



  return (
    <>
      <Grid container spacing={2} className="content-management">
        <Grid item xs={12}>
          <Paper
            elevation={0}
            sx={{
              p: 2,
              mb: 3,
              borderRadius: 3,
              border: "1px solid",
              borderColor: "divider",
              backgroundColor: "background.paper",
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
            }}
          >
            {/* Left: Title */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                📝 Manage Posts
              </Typography>
            </Box>

            {/* Middle: Filters + Search */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                flexWrap: "wrap",
                flex: 1,
                justifyContent: { xs: "flex-start", md: "center" },
                minWidth: 260,
              }}
            >
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Topic Tag</InputLabel>
                <Select
                  value={topicFilter}
                  label="Topic Tag"
                  onChange={handleTopicFilterChange}
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value="">All Topics</MenuItem>
                  {topicOptions.map((p) => (
                    <MenuItem key={p.value} value={p.value}>
                      {p.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Phase Tag</InputLabel>
                <Select
                  value={phaseFilter}
                  label="Phase Tag"
                  onChange={handlePhaseFilterChange}
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value="">All Phases</MenuItem>
                  {phaseOptions.map((p) => (
                    <MenuItem key={p.value} value={p.value}>
                      {p.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                label="Search Posts"
                variant="outlined"
                size="small"
                value={searchTerm}
                onChange={handleSearchChange}
                placeholder="Search by title or content"
                sx={{
                  minWidth: 220,
                  "& .MuiOutlinedInput-root": { borderRadius: 2 },
                }}
              />
            </Box>

            {/* Right: Create Button */}
            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setOpenCreateModal(true)}
                sx={{
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: 500,
                  px: 2.5,
                  py: 1,
                  whiteSpace: "nowrap",
                }}
                className="create-btn"
              >
                Create Post
              </Button>
            </Box>
          </Paper>


          <Grid container spacing={3} sx={{ marginTop: "8px" }}>
            {sortedPostsData.length > 0 ? (
              sortedPostsData.map((post) => (
                <Grid item xs={12} sm={6} md={4} key={post._id}>
                  <Card
                    sx={{
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      transition: "transform 0.2s, box-shadow 0.2s",
                      "&:hover": {
                        transform: "translateY(-4px)",
                        boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        p: 2,
                        borderBottom: "1px solid #eee",
                      }}
                    >
                      <Box display="flex" alignItems="center" gap={1}>
                        <Avatar
                          src={post?.author?.profileImage?.signedUrl}
                          alt={post?.author?.name}
                          sx={{ width: 40, height: 40 }}
                        />
                        <Box>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                            {post?.author?.name}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {formatDate(post?.createdAt)}
                          </Typography>
                        </Box>
                      </Box>
                      <IconButton
                        size="small"
                        onClick={(e) => handleMenuClick(e, post._id)}
                      >
                        <MoreVertIcon />
                      </IconButton>
                      <Menu
                        anchorEl={menuAnchorEls[post._id]}
                        open={Boolean(menuAnchorEls[post._id])}
                        onClose={() => handleMenuClose(post._id)}
                      >
                        <MenuItem onClick={() => handleView(post)}>
                          <VisibilityIcon sx={{ mr: 1, fontSize: 18 }} />
                          View Details
                        </MenuItem>
                        <MenuItem onClick={() => handleDelete(post._id)}>
                          <DeleteIcon sx={{ mr: 1, fontSize: 18, color: "error.main" }} />
                          Delete
                        </MenuItem>
                      </Menu>
                    </Box>

                    {post?.attachment?.signedUrl && (
                      <Box
                        sx={{
                          width: "100%",
                          backgroundColor: "#000",
                          aspectRatio: "1",
                          overflow: "hidden",
                          display: "flex",
                          height: "150px",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        {post.attachment.mimeType?.startsWith("video") ? (
                          <video
                            src={post.attachment.signedUrl}
                            style={{
                              width: "100%",
                              height: "150px",
                              objectFit: "cover",
                            }}
                            controls
                          />
                        ) : (
                          <img
                            src={post.attachment.signedUrl}
                            alt={post.title}
                            style={{
                              width: "100%",
                              height: "150px",
                              objectFit: "cover",
                            }}
                          />
                        )}
                      </Box>
                    )}

                    <CardContent sx={{ pb: 1, flexGrow: 1 }}>
                      {post?.title && (
                        <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                          {post.title}
                        </Typography>
                      )}
                      <Typography
                        variant="body2"
                        color="textSecondary"
                        sx={{
                          mb: 2,
                          display: "-webkit-box",
                          overflow: "hidden",
                          WebkitBoxOrient: "vertical",
                          WebkitLineClamp: 3,
                        }}
                      >
                        {post?.content}
                      </Typography>

                      <Box display="flex" gap={1} flexWrap="wrap" sx={{ mb: 1.5 }}>
                        {post?.topicTag && (
                          <Chip label={post.topicTag} size="small" variant="outlined" />
                        )}
                        {post?.phaseTag && (
                          <Chip label={post.phaseTag} size="small" variant="outlined" />
                        )}
                      </Box>
                    </CardContent>

                    <CardActions
                      sx={{
                        display: "flex",
                        justifyContent: "space-around",
                        borderTop: "1px solid #eee",
                        pt: 1,
                        pb: 1,
                      }}
                    >
                      <Box display="flex" alignItems="center" gap={0.5} sx={{ cursor: 'pointer' }} onClick={(e) => openPopover(e.currentTarget, post, 'likes')}>
                        <FavoriteBorderIcon sx={{ fontSize: 20, color: "textSecondary" }} />
                        <Typography variant="caption">{post?.likes?.length || 0}</Typography>
                      </Box>
                      <Box display="flex" alignItems="center" gap={0.5} sx={{ cursor: 'pointer' }} onClick={(e) => openPopover(e.currentTarget, post, 'comments')}>
                        <ChatBubbleOutlineIcon sx={{ fontSize: 20, color: "textSecondary" }} />
                        <Typography variant="caption">{post?.comments?.length || 0}</Typography>
                      </Box>
                      <Box display="flex" alignItems="center" gap={0.5} sx={{ cursor: 'pointer' }} onClick={(e) => openPopover(e.currentTarget, post, 'reports')}>
                        <FlagIcon
                          sx={{
                            fontSize: 20,
                            color: post?.reports?.length > 0 ? "error.main" : "textSecondary",
                          }}
                        />
                        <Typography
                          variant="caption"
                          color={post?.reports?.length > 0 ? "error" : "textSecondary"}
                        >
                          {post?.reports?.length || 0}
                        </Typography>
                      </Box>
                    </CardActions>
                  </Card>
                </Grid>
              ))
            ) : (
              <Grid item xs={12}>
                <Paper sx={{ p: 4, textAlign: "center" }}>
                  <Typography variant="body2" color="textSecondary">
                    No posts found
                  </Typography>
                </Paper>
              </Grid>
            )}
          </Grid>
        </Grid>
        <Grid container justifyContent="center" mt={3}>
          <Pagination
            count={Math.ceil(totalPages)}
            color="secondary"
            size="small"
            page={page + 1}
            onChange={handleChangePage}
          />
        </Grid>

      </Grid>

      <ConfirmDeleteDialog
        open={openConfirmDialog}
        onClose={() => setOpenConfirmDialog(false)}
        onConfirm={confirmDeletePost}
        title="Delete Post"
        description="Are you sure you want to delete this post? This action cannot be undone."
      />

      <Popover
        open={Boolean(popAnchor)}
        anchorEl={popAnchor}
        onClose={closePopover}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{ elevation: 6, sx: { borderRadius: 2, width: 320, maxHeight: 360, overflowY: 'auto', p: 1 } }}
      >
        <Box sx={{ px: 1, pt: 1, pb: 0.5 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            {popType === 'likes' && `Likes (${popPost?.likes?.length || 0})`}
            {popType === 'comments' && `Comments (${popPost?.comments?.length || 0})`}
            {popType === 'reports' && `Reports (${popPost?.reports?.length || 0})`}
          </Typography>
        </Box>
        <Box sx={{ pt: 0 }}>
          {renderEngagementList(popPost, popType)}
        </Box>
      </Popover>

      <Dialog
        open={openDetailDialog}
        onClose={() => setOpenDetailDialog(false)}
        maxWidth="md"
        fullWidth
        sx={{
          "& .MuiDialog-paper": {
            borderRadius: 3,
            overflow: "hidden",
          },
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: 600,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderBottom: "1px solid",
            borderColor: "divider",
            pb: 1.5,
          }}
        >
          Post Details
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          {selectedPost ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
              {/* 🧍 Author Section */}
              <Paper
                variant="outlined"
                sx={{
                  p: 2.5,
                  borderRadius: 2,
                  display: "flex",
                  flexDirection: "column",
                  gap: 1,
                }}
              >
                <Typography variant="h6" fontWeight={600}>
                  Author Details
                </Typography>
                <Typography variant="body2">
                  <strong>Name:</strong> {selectedPost?.author?.name || "-"}
                </Typography>
                <Typography variant="body2">
                  <strong>Email:</strong> {selectedPost?.author?.email || "-"}
                </Typography>
              </Paper>

              {/* 📝 Post Content */}
              <Paper
                variant="outlined"
                sx={{
                  p: 2.5,
                  borderRadius: 2,
                  display: "flex",
                  flexDirection: "column",
                  gap: 1.5,
                }}
              >
                <Typography variant="h6" fontWeight={600}>
                  Post Information
                </Typography>
                <Typography variant="body2">
                  <strong>Title:</strong> {selectedPost?.title || "Untitled"}
                </Typography>
                <Box>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Content:</strong>
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      backgroundColor: "#f8f9fa",
                      p: 2,
                      borderRadius: 2,
                      whiteSpace: "pre-line",
                    }}
                  >
                    {selectedPost?.content || "-"}
                  </Typography>
                </Box>

                {/* 📸 Media */}
                {selectedPost?.attachment?.signedUrl && (
                  <Box>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Attached Media:</strong>
                    </Typography>
                    {selectedPost.attachment.mimeType?.startsWith("video") ? (
                      <video
                        src={selectedPost.attachment.signedUrl}
                        style={{
                          width: "100%",
                          borderRadius: "10px",
                          backgroundColor: "#000",
                          maxHeight: 400,
                        }}
                        controls
                      />
                    ) : (
                      <img
                        src={selectedPost.attachment.signedUrl}
                        alt={selectedPost.title}
                        style={{
                          width: "100%",
                          borderRadius: "10px",
                          objectFit: "cover",
                          maxHeight: 400,
                        }}
                      />
                    )}
                  </Box>
                )}

                <Typography variant="body2" sx={{ mt: 1 }}>
                  <strong>Posted:</strong> {formatDate(selectedPost?.createdAt)}
                </Typography>

                <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", mt: 1 }}>
                  {selectedPost?.topicTag && (
                    <Chip
                      label={`Topic: ${selectedPost.topicTag}`}
                      color="primary"
                      variant="outlined"
                      size="small"
                    />
                  )}
                  {selectedPost?.phaseTag && (
                    <Chip
                      label={`Phase: ${selectedPost.phaseTag}`}
                      color="secondary"
                      variant="outlined"
                      size="small"
                    />
                  )}
                </Box>
              </Paper>

              {/* 📊 Engagement Stats */}
              <Paper
                variant="outlined"
                sx={{
                  p: 2.5,
                  borderRadius: 2,
                }}
              >
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  Engagement Overview
                </Typography>

                <Grid container spacing={2}>
                  {[
                    { label: "Likes", value: selectedPost?.likes?.length || 0 },
                    { label: "Comments", value: selectedPost?.comments?.length || 0 },
                    { label: "Reports", value: selectedPost?.reports?.length || 0 },
                  ].map((stat, i) => (
                    <Grid item xs={4} key={i}>
                      <Paper
                        elevation={0}
                        sx={{
                          p: 2,
                          textAlign: "center",
                          borderRadius: 2,
                          backgroundColor: "#fafafa",
                        }}
                      >
                        <Typography variant="h6" fontWeight={600}>
                          {stat.value}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ textTransform: "uppercase" }}
                        >
                          {stat.label}
                        </Typography>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              </Paper>

              {/* 🚨 Reports Section */}
              {selectedPost?.reports?.length > 0 && (
                <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
                  <Typography variant="h6" fontWeight={600} gutterBottom>
                    Reports ({selectedPost.reports.length})
                  </Typography>
                  {selectedPost.reports.map((report, idx) => (
                    <Paper
                      key={idx}
                      elevation={0}
                      sx={{
                        p: 2,
                        mb: 1.5,
                        borderRadius: 2,
                        backgroundColor: "#f9f9f9",
                      }}
                    >
                      <Typography variant="body2">
                        <strong>Reported by:</strong> {report?.user?.name || "-"}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Email:</strong> {report?.user?.email || "-"}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Reason:</strong> {report?.reason || "-"}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatDate(report?.createdAt)}
                      </Typography>
                    </Paper>
                  ))}
                </Paper>
              )}

              {/* 💬 Comments Section */}
              {selectedPost?.comments?.length > 0 && (
                <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
                  <Typography variant="h6" fontWeight={600} gutterBottom>
                    Recent Comments ({selectedPost.comments.length})
                  </Typography>
                  {selectedPost.comments.slice(0, 5).map((comment, idx) => (
                    <Paper
                      key={idx}
                      elevation={0}
                      sx={{
                        p: 2,
                        mb: 1.5,
                        borderRadius: 2,
                        backgroundColor: "#f9f9f9",
                      }}
                    >
                      <Typography variant="body2">
                        <strong>{comment?.user?.name}:</strong> {comment?.text}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatDate(comment?.createdAt)} ·{" "}
                        {comment?.likes?.length || 0} likes
                      </Typography>
                    </Paper>
                  ))}
                </Paper>
              )}
            </Box>
          ) : (
            <Typography textAlign="center" sx={{ py: 3 }}>
              No post selected.
            </Typography>
          )}
        </DialogContent>

        <DialogActions
          sx={{
            px: 3,
            py: 2,
            borderTop: "1px solid",
            borderColor: "divider",
            justifyContent: "flex-end",
          }}
        >
          <Button onClick={() => setOpenDetailDialog(false)} variant="outlined">
            Close
          </Button>
        </DialogActions>
      </Dialog>


      <CreatePostModal
        open={openCreateModal}
        onClose={() => setOpenCreateModal(false)}
        onPostCreated={() => {
          getAllPostsData();
          setOpenCreateModal(false);
        }}
      />
    </>
  );
};

export default PostsManager;
